import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ConstructionInspectionsService } from '../services/inspections.service';
import type { ConstructionInspection, InspectionFilters } from '../types/construction-inspections.types';
import { useState } from 'react';
import { toast } from 'sonner';

export function useConstructionInspections(companyId: string, initialFilters: InspectionFilters = {}) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<InspectionFilters>({
    status: 'all',
    type: 'all',
    ...initialFilters
  });

  const query = useQuery({
    queryKey: ['construction-inspections', companyId, filters],
    queryFn: () => ConstructionInspectionsService.getInspections(companyId, filters),
    enabled: !!companyId,
  });

  const statsQuery = useQuery({
    queryKey: ['construction-inspection-stats', companyId, filters.projectId],
    queryFn: () => ConstructionInspectionsService.getInspectionStats(companyId, filters.projectId),
    enabled: !!companyId,
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<ConstructionInspection>) => ConstructionInspectionsService.createInspection(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['construction-inspections'] });
      toast.success('Khởi tạo yêu cầu kiểm tra thành công');
    },
  });

  return {
    inspections: query.data?.data || [],
    total: query.data?.total || 0,
    stats: statsQuery.data,
    isLoading: query.isLoading || statsQuery.isLoading,
    filters,
    setFilters,
    createInspection: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
  };
}

export function useConstructionInspectionDetail(id?: string) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['construction-inspections', 'detail', id],
    queryFn: () => ConstructionInspectionsService.getInspectionDetail(id!),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (updates: Partial<ConstructionInspection>) => ConstructionInspectionsService.updateInspection(id!, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['construction-inspections'] });
      queryClient.invalidateQueries({ queryKey: ['construction-inspections', 'detail', id] });
      toast.success('Cập nhật phiếu kiểm tra thành công');
    },
  });

  return {
    inspection: query.data,
    isLoading: query.isLoading,
    updateInspection: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
  };
}
